import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  Button,
  Select,
  SelectItem,
  Label,
  Textarea,
  DateInput,
} from '../../../components/ui';
import { PlusIcon, TrashIcon } from '../../../components/ui/Icons';
import { useFlexibleList } from '../../../hooks/useSWR';

const emptyRow = () => ({
  description: '',
  responsibleType: 'USER',
  responsibleUserId: '',
  responsibleRoleId: '',
  dueDate: '',
  isCompleted: false,
});

const formatDateForInput = (date) => {
  if (!date) return '';
  try {
    return String(date).slice(0, 10);
  } catch {
    return '';
  }
};

/**
 * Actions Required rows for Change Control.
 * Section title comes from formSections (same group header as Classification & Approvals).
 */
const ChangeControlActionsIntegration = ({
  formData,
  setFormData,
  config,
  isEditMode = false,
  selectedRecord = null,
}) => {
  const { theme } = useTheme();
  const isViewMode = selectedRecord && !isEditMode && !setFormData;
  const [localActions, setLocalActions] = useState([]);
  const initializedRef = useRef(false);
  const recordIdRef = useRef(null);

  const { data: users = [] } = useFlexibleList(
    config?.api?.lookupEndpoints?.users || '/users',
  );
  const { data: roles = [] } = useFlexibleList(
    config?.api?.lookupEndpoints?.roles || '/role',
  );
  const userList = (Array.isArray(users) ? users : []).filter(
    (u) => u?.isQMS === true,
  );
  const roleList = Array.isArray(roles) ? roles : [];

  useEffect(() => {
    const recordId = selectedRecord?.id || formData?.id || null;
    if (recordIdRef.current !== recordId) {
      initializedRef.current = false;
      recordIdRef.current = recordId;
    }
    if (initializedRef.current) return;

    const source =
      (Array.isArray(formData?.actions) && formData.actions.length > 0
        ? formData.actions
        : null) ||
      (Array.isArray(selectedRecord?.actions) && selectedRecord.actions.length > 0
        ? selectedRecord.actions
        : null) ||
      [];

    const mapped = source.map((a) => {
      const type =
        a.responsibleType === 'ROLE' || a.responsibleRoleId
          ? 'ROLE'
          : 'USER';
      return {
        id: a.id,
        description: a.description || '',
        responsibleType: type,
        responsibleUserId:
          a.responsibleUserId != null && a.responsibleUserId !== ''
            ? String(a.responsibleUserId)
            : '',
        responsibleRoleId:
          a.responsibleRoleId != null && a.responsibleRoleId !== ''
            ? String(a.responsibleRoleId)
            : '',
        dueDate: formatDateForInput(a.dueDate),
        isCompleted: !!a.isCompleted,
        recordOrder: a.recordOrder,
      };
    });

    setLocalActions(mapped.length > 0 ? mapped : isViewMode ? [] : [emptyRow()]);
    initializedRef.current = true;
  }, [formData?.actions, formData?.id, selectedRecord, isViewMode]);

  const syncToForm = (next) => {
    setLocalActions(next);
    if (typeof setFormData === 'function') {
      setFormData((prev) => ({
        ...prev,
        actions: next.map((a, idx) => {
          const type = a.responsibleType === 'ROLE' ? 'ROLE' : 'USER';
          return {
            ...(a.id ? { id: a.id } : {}),
            description: a.description || '',
            responsibleType: type,
            responsibleUserId:
              type === 'USER' &&
              a.responsibleUserId !== '' &&
              a.responsibleUserId != null
                ? parseInt(a.responsibleUserId, 10)
                : null,
            responsibleRoleId:
              type === 'ROLE' &&
              a.responsibleRoleId !== '' &&
              a.responsibleRoleId != null
                ? parseInt(a.responsibleRoleId, 10)
                : null,
            dueDate: a.dueDate || null,
            isCompleted: !!a.isCompleted,
            recordOrder: idx + 1,
          };
        }),
      }));
    }
  };

  const updateRow = (index, patch) => {
    const next = localActions.map((row, i) =>
      i === index ? { ...row, ...patch } : row,
    );
    syncToForm(next);
  };

  const addRow = () => {
    syncToForm([...localActions, emptyRow()]);
  };

  const removeRow = (index) => {
    const next = localActions.filter((_, i) => i !== index);
    syncToForm(next.length > 0 ? next : [emptyRow()]);
  };

  const userLabel = (id) => {
    if (id == null || id === '') return '—';
    const u = userList.find((x) => Number(x.id) === Number(id));
    return u?.userDisplayName || u?.name || `#${id}`;
  };

  const roleLabel = (id) => {
    if (id == null || id === '') return '—';
    const r = roleList.find((x) => Number(x.id) === Number(id));
    return r?.name || `#${id}`;
  };

  const responsibleLabel = (row) => {
    if (row.responsibleType === 'ROLE') {
      const name = roleLabel(row.responsibleRoleId);
      return name === '—' ? '—' : `Group: ${name}`;
    }
    const name = userLabel(row.responsibleUserId);
    return name === '—' ? '—' : `User: ${name}`;
  };

  if (isViewMode) {
    if (localActions.length === 0) {
      return (
        <div className={cn('text-sm', theme.text.muted)}>No actions recorded</div>
      );
    }
    return (
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className={cn(theme.colors.backgroundAlt)}>
              <th className="text-left p-2 border-b">Action</th>
              <th className="text-left p-2 border-b w-56">Responsible</th>
              <th className="text-left p-2 border-b w-36">Due date</th>
            </tr>
          </thead>
          <tbody>
            {localActions.map((row, idx) => (
              <tr key={row.id || idx} className="border-b last:border-0">
                <td className="p-2 align-top whitespace-pre-wrap">
                  {row.description || '—'}
                </td>
                <td className="p-2 align-top">{responsibleLabel(row)}</td>
                <td className="p-2 align-top">{row.dueDate || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add action
        </Button>
      </div>

      <div className="space-y-3">
        {localActions.map((row, index) => (
          <div
            key={row.id || `new-${index}`}
            className={cn(
              'grid grid-cols-1 md:grid-cols-12 gap-3 p-3 rounded-lg border',
              theme.border.subtle,
              theme.colors.backgroundAlt,
            )}
          >
            <div className="md:col-span-5 space-y-1">
              <Label>Action</Label>
              <Textarea
                rows={2}
                value={row.description}
                onChange={(e) =>
                  updateRow(index, { description: e.target.value })
                }
                placeholder="Describe the required action"
              />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Type</Label>
              <Select
                value={row.responsibleType || 'USER'}
                onValueChange={(val) =>
                  updateRow(index, {
                    responsibleType: val || 'USER',
                    responsibleUserId: '',
                    responsibleRoleId: '',
                  })
                }
              >
                <SelectItem value="USER">User</SelectItem>
                <SelectItem value="ROLE">Group</SelectItem>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Responsible</Label>
              {row.responsibleType === 'ROLE' ? (
                <Select
                  value={row.responsibleRoleId || ''}
                  onValueChange={(val) =>
                    updateRow(index, { responsibleRoleId: val })
                  }
                  placeholder="Select group"
                >
                  <SelectItem value="">—</SelectItem>
                  {roleList.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.name || `Group ${r.id}`}
                    </SelectItem>
                  ))}
                </Select>
              ) : (
                <Select
                  value={row.responsibleUserId || ''}
                  onValueChange={(val) =>
                    updateRow(index, { responsibleUserId: val })
                  }
                  placeholder="Select user"
                >
                  <SelectItem value="">—</SelectItem>
                  {userList.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.userDisplayName || u.name || `User ${u.id}`}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Due date</Label>
              <DateInput
                value={formatDateForInput(row.dueDate)}
                onChange={(e) =>
                  updateRow(index, {
                    dueDate: e?.target?.value || '',
                  })
                }
              />
            </div>
            <div className="md:col-span-1 flex items-end justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => removeRow(index)}
                title="Remove action"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChangeControlActionsIntegration;
